/**
 * A boss fight as a pure simulation, stepped at the game's 20 ticks a second
 * (ui/bossFight.ts draws it and feeds it taps). Nothing here touches the DOM
 * or the clock, so every rule can be tested.
 *
 * The loop: tap the boss to hit it; quick taps build a combo; a gold weak
 * point opens now and then for a critical. The boss winds up moves with a
 * warning, and a Brace any time during the wind-up blocks them: the skill is
 * seeing the warning and answering it (a first version wanted the Brace in
 * the last half second, and reacting straight away was always too early). A Death
 * Roll has to be tapped out of; a Submerge hides the boss under one of three
 * ripples. Hits charge a special, one per species. Empty the boss's health
 * before the clock runs out, or before the dinosaur's hearts do.
 */

import {
  BOSSES_BY_ID,
  BOSS_MIN_LEVEL,
  BOSS_REST_SECONDS,
  VICTORY_ROAR_MULT,
  VICTORY_ROAR_SECONDS,
} from '../data/bosses';
import type { BossDef, BossMoveId } from '../data/bosses';
import type { GameState } from './state';
import { TICKS_PER_SECOND } from './loop';

const T = TICKS_PER_SECOND;

export const HEARTS = 4;
/** What one tap does, before the dinosaur's power and the combo. */
export const TAP_DAMAGE = 10;
/** A tap on the open weak point does this many times as much. */
export const CRIT_MULT = 5;
/** Taps closer together than this keep the combo going. */
export const COMBO_WINDOW_TICKS = Math.round(0.6 * T);
/** Each step of combo adds this much damage, up to COMBO_MAX steps. */
export const COMBO_STEP = 0.025;
export const COMBO_MAX = 20;
/** Ticks before Brace can be pressed again, so it cannot simply be held down. */
export const BRACE_COOLDOWN_TICKS = Math.round(0.8 * T);
/** The special's meter: what fills it, and what it takes. */
export const METER_FULL = 100;
export const METER_PER_TAP = 1.5;
export const METER_PER_CRIT = 6;
export const METER_PER_BLOCK = 20;
/** The weak point: how long it stays open, and the wait between openings. */
export const WEAK_OPEN_TICKS = Math.round(1.2 * T);
export const WEAK_GAP = { min: Math.round(2.5 * T), max: Math.round(4.5 * T) };
/** A Death Roll: taps to break free, and the ticks to do it in. */
export const ROLL_TAPS = 12;
export const ROLL_TICKS = Math.round(2.5 * T);
/** Submerge: how long it stays under, and how long it is dazed if found. */
export const SUBMERGE_TICKS = Math.round(3 * T);
export const DAZED_TICKS = Math.round(3 * T);
export const RIPPLES = 3;
/** The last seconds of the clock, when the boss enrages. */
export const ENRAGE_SECONDS = 15;
const ENRAGE_SPEED = 0.7;
/** A burst out of the water after a missed Submerge: a short wind-up. */
const BURST_WINDUP_TICKS = Math.round(0.6 * T);

export type SpecialId = 'charge' | 'spikes' | 'ambush' | 'bite';

export interface SpecialDef {
  id: SpecialId;
  name: string;
  what: string;
}

export const SPECIALS: Readonly<Record<SpecialId, SpecialDef>> = {
  charge: {
    id: 'charge',
    name: 'Charge',
    what: 'A running headbutt: heavy damage, and the boss is staggered out of its wind-up.',
  },
  spikes: {
    id: 'spikes',
    name: 'Spike Wall',
    what: 'The next attack is blocked on its own, and half of it goes back.',
  },
  ambush: {
    id: 'ambush',
    name: 'Ambush',
    what: 'A huge hit from the water, doubled against anything that lives there.',
  },
  bite: { id: 'bite', name: 'Power Bite', what: 'One big bite.' },
};

/** The special each dinosaur brings; the rest bite. Evolved forms share their tier's. */
export function specialFor(dinoId: string): SpecialId {
  if (dinoId === 'triceratops') return 'charge';
  if (dinoId === 'stegosaurus') return 'spikes';
  if (dinoId === 'mosasaurus') return 'ambush';
  return 'bite';
}

/**
 * How hard a dinosaur hits: its level over 100, a little more for each tier
 * up the roster, and half as much again once evolved.
 */
export function fightPower(tier: number, level: number, evolved: boolean): number {
  return (level / 100) * (1 + tier * 0.04) * (evolved ? 1.5 : 1);
}

/** Something that happened this tick, for the view to show. */
export type FightEvent =
  | { kind: 'hit'; damage: number; crit: boolean }
  | { kind: 'blocked'; reflected: number }
  | { kind: 'hurt'; hearts: number }
  | { kind: 'windup'; move: BossMoveId }
  | { kind: 'rolled' }
  | { kind: 'escaped' }
  | { kind: 'submerged' }
  | { kind: 'found' }
  | { kind: 'missed' }
  | { kind: 'special'; special: SpecialId; damage: number }
  | { kind: 'weak' }
  | { kind: 'enraged' }
  | { kind: 'over'; won: boolean };

export interface Fight {
  boss: BossDef;
  power: number;
  special: SpecialId;
  tick: number;
  ticksLeft: number;
  bossHp: number;
  hearts: number;
  combo: number;
  lastTapTick: number;
  meter: number;
  /** What the boss is doing. */
  phase: 'idle' | 'windup' | 'roll' | 'submerged' | 'over';
  /** The move being wound up, and the ticks until it lands. */
  move: BossMoveId | null;
  moveTicks: number;
  /** Ticks until the next wind-up starts, while idle. */
  nextIn: number;
  braceAt: number;
  /** The tick the current wind-up began; a Brace since then blocks it. */
  windupStart: number;
  braceReadyAt: number;
  /** The open weak point, as a share of the boss's box, or null. */
  weak: { x: number; y: number; ticksLeft: number } | null;
  weakIn: number;
  /** Taps left to break a Death Roll, and ticks to do it in. */
  rollTaps: number;
  rollTicks: number;
  /** The ripples while submerged (0-1 across the water) and which one is real. */
  ripples: number[];
  realRipple: number;
  submergeTicks: number;
  dazedTicks: number;
  /** Spike Wall: ticks during which the next attack is blocked on its own. */
  shieldTicks: number;
  enraged: boolean;
  hitsTaken: number;
  damageDone: number;
  result: 'won' | 'lost' | null;
  events: FightEvent[];
  rand: () => number;
}

export function startFight(
  boss: BossDef,
  dino: { id: string; tier: number; level: number; evolved: boolean },
  rand: () => number = Math.random,
): Fight {
  return {
    boss,
    power: fightPower(dino.tier, dino.level, dino.evolved),
    special: specialFor(dino.id),
    tick: 0,
    ticksLeft: boss.seconds * T,
    bossHp: boss.health,
    hearts: HEARTS,
    combo: 0,
    lastTapTick: -999,
    meter: 0,
    phase: 'idle',
    move: null,
    moveTicks: 0,
    // A moment to get your bearings before the first move.
    nextIn: Math.round(2 * T),
    braceAt: -999,
    windupStart: 0,
    braceReadyAt: 0,
    weak: null,
    weakIn: Math.round(1.5 * T),
    rollTaps: 0,
    rollTicks: 0,
    ripples: [],
    realRipple: 0,
    submergeTicks: 0,
    dazedTicks: 0,
    shieldTicks: 0,
    enraged: false,
    hitsTaken: 0,
    damageDone: 0,
    result: null,
    events: [],
    rand,
  };
}

const pace = (f: Fight, ticks: number): number =>
  Math.round(ticks * (f.enraged ? ENRAGE_SPEED : 1));

function damageBoss(f: Fight, amount: number): number {
  const dealt = Math.max(1, Math.round(amount * (f.dazedTicks > 0 ? 2 : 1)));
  f.bossHp = Math.max(0, f.bossHp - dealt);
  f.damageDone += dealt;
  if (f.bossHp === 0) finish(f, true);
  return dealt;
}

function finish(f: Fight, won: boolean): void {
  if (f.phase === 'over') return;
  f.phase = 'over';
  f.result = won ? 'won' : 'lost';
  f.weak = null;
  f.events.push({ kind: 'over', won });
}

function hurt(f: Fight, hearts: number): void {
  f.hearts = Math.max(0, f.hearts - hearts);
  f.hitsTaken++;
  f.combo = 0;
  f.events.push({ kind: 'hurt', hearts: f.hearts });
  if (f.hearts === 0) finish(f, false);
}

function pickMove(f: Fight): BossMoveId {
  const total = f.boss.moves.reduce((s, m) => s + m.weight, 0);
  let roll = f.rand() * total;
  for (const m of f.boss.moves) {
    roll -= m.weight;
    if (roll < 0) return m.id;
  }
  return f.boss.moves[0]!.id;
}

function beginWindup(f: Fight, move: BossMoveId, ticks?: number): void {
  const def = f.boss.moves.find((m) => m.id === move);
  f.phase = 'windup';
  f.move = move;
  f.windupStart = f.tick;
  f.moveTicks = ticks ?? pace(f, Math.round((def?.windup ?? 1) * T));
  f.events.push({ kind: 'windup', move });
}

/** Whether a Brace (or Spike Wall) takes this attack. */
function blocks(f: Fight): boolean {
  if (f.shieldTicks > 0) return true;
  return f.braceAt >= f.windupStart;
}

function land(f: Fight): void {
  const move = f.move;
  f.move = null;
  f.phase = 'idle';
  f.nextIn = pace(
    f,
    Math.round((f.boss.gap.min + f.rand() * (f.boss.gap.max - f.boss.gap.min)) * T),
  );
  if (move === 'submerge') {
    // Not an attack: it goes under, and one of three ripples is really it.
    f.phase = 'submerged';
    f.submergeTicks = pace(f, SUBMERGE_TICKS);
    f.ripples = Array.from({ length: RIPPLES }, (_, i) => (i + 0.3 + f.rand() * 0.4) / RIPPLES);
    f.realRipple = Math.floor(f.rand() * RIPPLES);
    f.weak = null;
    f.events.push({ kind: 'submerged' });
    return;
  }
  if (blocks(f)) {
    const spikes = f.shieldTicks > 0;
    f.shieldTicks = 0;
    const reflected = spikes ? damageBoss(f, 15 * TAP_DAMAGE * f.power) : 0;
    f.meter = Math.min(METER_FULL, f.meter + METER_PER_BLOCK);
    f.events.push({ kind: 'blocked', reflected });
    return;
  }
  if (move === 'roll') {
    f.phase = 'roll';
    f.rollTaps = ROLL_TAPS;
    f.rollTicks = ROLL_TICKS;
    f.weak = null;
    f.events.push({ kind: 'rolled' });
    return;
  }
  hurt(f, 1);
}

/** One tick of the fight. */
export function tickFight(f: Fight): void {
  if (f.phase === 'over') return;
  f.tick++;
  f.ticksLeft--;
  if (!f.enraged && f.ticksLeft <= ENRAGE_SECONDS * T) {
    f.enraged = true;
    f.events.push({ kind: 'enraged' });
  }
  if (f.ticksLeft <= 0) {
    finish(f, false);
    return;
  }
  if (f.tick - f.lastTapTick > COMBO_WINDOW_TICKS) f.combo = 0;
  if (f.dazedTicks > 0) f.dazedTicks--;
  if (f.shieldTicks > 0) f.shieldTicks--;

  // The weak point opens and closes on its own clock, while the boss is up.
  if (f.phase === 'idle' || f.phase === 'windup') {
    if (f.weak) {
      if (--f.weak.ticksLeft <= 0) {
        f.weak = null;
        f.weakIn = WEAK_GAP.min + Math.floor(f.rand() * (WEAK_GAP.max - WEAK_GAP.min));
      }
    } else if (--f.weakIn <= 0) {
      f.weak = { x: 0.2 + f.rand() * 0.55, y: 0.25 + f.rand() * 0.35, ticksLeft: WEAK_OPEN_TICKS };
      f.events.push({ kind: 'weak' });
    }
  }

  switch (f.phase) {
    case 'idle':
      if (--f.nextIn <= 0) beginWindup(f, pickMove(f));
      break;
    case 'windup':
      if (--f.moveTicks <= 0) land(f);
      break;
    case 'roll':
      if (--f.rollTicks <= 0) {
        f.phase = 'idle';
        hurt(f, 2);
      }
      break;
    case 'submerged':
      if (--f.submergeTicks <= 0) {
        // Not found in time: it bursts out and snaps.
        f.ripples = [];
        f.events.push({ kind: 'missed' });
        beginWindup(f, 'snap', BURST_WINDUP_TICKS);
      }
      break;
  }
}

/** A tap on the boss; \`onWeak\` when it landed on the open weak point. */
export function tapBoss(f: Fight, onWeak: boolean): void {
  if (f.phase === 'over' || f.phase === 'submerged') return;
  if (f.phase === 'roll') {
    // While rolled, every tap is a struggle to get free, not a hit.
    if (--f.rollTaps <= 0) {
      f.phase = 'idle';
      f.events.push({ kind: 'escaped' });
    }
    return;
  }
  f.combo = f.tick - f.lastTapTick <= COMBO_WINDOW_TICKS ? Math.min(COMBO_MAX, f.combo + 1) : 1;
  f.lastTapTick = f.tick;
  const crit = onWeak && f.weak !== null;
  const base = TAP_DAMAGE * f.power * (1 + f.combo * COMBO_STEP) * (crit ? CRIT_MULT : 1);
  f.meter = Math.min(METER_FULL, f.meter + (crit ? METER_PER_CRIT : METER_PER_TAP));
  const damage = damageBoss(f, base);
  f.events.push({ kind: 'hit', damage, crit });
}

/** A tap while the boss is under: on a ripple (its index), or anywhere else (null). */
export function tapRipple(f: Fight, index: number | null): void {
  if (f.phase !== 'submerged') return;
  f.ripples = [];
  if (index === f.realRipple) {
    f.phase = 'idle';
    f.dazedTicks = DAZED_TICKS;
    f.nextIn = pace(f, Math.round(f.boss.gap.max * T));
    f.events.push({ kind: 'found' });
    return;
  }
  f.events.push({ kind: 'missed' });
  beginWindup(f, 'snap', BURST_WINDUP_TICKS);
}

export function brace(f: Fight): void {
  if (f.phase === 'over' || f.tick < f.braceReadyAt) return;
  f.braceAt = f.tick;
  f.braceReadyAt = f.tick + BRACE_COOLDOWN_TICKS;
}

/** Whether a Brace pressed now would still be counting (for drawing the guard). */
export function bracing(f: Fight): boolean {
  return (f.phase === 'windup' && f.braceAt >= f.windupStart) || f.shieldTicks > 0;
}

export function specialReady(f: Fight): boolean {
  return f.meter >= METER_FULL && (f.phase === 'idle' || f.phase === 'windup');
}

export function useSpecial(f: Fight): void {
  if (!specialReady(f)) return;
  f.meter = 0;
  let damage = 0;
  switch (f.special) {
    case 'charge':
      damage = damageBoss(f, 25 * TAP_DAMAGE * f.power);
      // Staggered: whatever it was winding up is lost.
      if (f.phase === 'windup') {
        f.phase = 'idle';
        f.move = null;
        f.nextIn = pace(f, Math.round(1.5 * T));
      }
      break;
    case 'spikes':
      f.shieldTicks = Math.round(6 * T);
      break;
    case 'ambush':
      damage = damageBoss(f, 30 * TAP_DAMAGE * f.power * (f.boss.water ? 2 : 1));
      break;
    case 'bite':
      damage = damageBoss(f, 20 * TAP_DAMAGE * f.power);
      break;
  }
  f.events.push({ kind: 'special', special: f.special, damage });
}

export function flee(f: Fight): void {
  finish(f, false);
}

/** Events since the last call, for the view; clears them. */
export function drainEvents(f: Fight): FightEvent[] {
  const out = f.events;
  f.events = [];
  return out;
}

// ---------------------------------------------------------------------------
// The fight against the save: who may fight, the rest, and the rewards.

/** Seconds a dinosaur still has to rest before it can fight; 0 when ready. */
export function restLeftSeconds(state: GameState, dinoId: string, now: number): number {
  return Math.max(0, ((state.bossRest[dinoId] ?? 0) - now) / 1000);
}

export function canFight(
  dinoId: string,
  level: number,
  state: GameState,
  now: number,
): { ok: true } | { ok: false; why: 'level' | 'resting' } {
  if (level < BOSS_MIN_LEVEL) return { ok: false, why: 'level' };
  if (restLeftSeconds(state, dinoId, now) > 0) return { ok: false, why: 'resting' };
  return { ok: true };
}

/** The title a dinosaur holds this era: its latest boss's, or null. */
export function titleOf(state: GameState, dinoId: string): string | null {
  const wins = state.bossWins[dinoId] ?? [];
  const last = wins[wins.length - 1];
  return last ? (BOSSES_BY_ID.get(last)?.title ?? null) : null;
}

export interface FightOutcome {
  won: boolean;
  flawless: boolean;
  shards: number;
  /** Fossils for the first time this boss was ever beaten; 0 after. */
  fossils: number;
  /** The title newly earned this era, or null (lost, or already held). */
  title: string | null;
  roar: boolean;
}

/**
 * Writes a finished fight into the save: the dinosaur rests either way; a
 * win pays shards (double if flawless), the Victory Roar, the boss's title
 * for this era, and, the first time the boss is ever beaten, its fossils.
 */
export function settleFight(state: GameState, f: Fight, dinoId: string, now: number): FightOutcome {
  state.bossRest[dinoId] = now + BOSS_REST_SECONDS * 1000;
  const won = f.result === 'won';
  const flawless = won && f.hitsTaken === 0;
  const outcome: FightOutcome = { won, flawless, shards: 0, fossils: 0, title: null, roar: false };
  if (!won) return outcome;
  outcome.shards = f.boss.shards * (flawless ? 2 : 1);
  state.shards += outcome.shards;
  state.shardsEarned += outcome.shards;
  // The Roar is a production boost: it replaces a weaker one, never a stronger.
  const current = state.boost && state.boost.endsAt > now ? state.boost.mult : 1;
  if (VICTORY_ROAR_MULT >= current) {
    state.boost = {
      kind: 'roar',
      mult: VICTORY_ROAR_MULT,
      endsAt: now + VICTORY_ROAR_SECONDS * 1000,
    };
    outcome.roar = true;
  }
  const wins = (state.bossWins[dinoId] ??= []);
  if (!wins.includes(f.boss.id)) {
    wins.push(f.boss.id);
    outcome.title = f.boss.title;
  }
  if (!state.bossTrophies.includes(f.boss.id)) {
    state.bossTrophies.push(f.boss.id);
    outcome.fossils = f.boss.fossils;
    state.fossils += outcome.fossils;
    state.fossilsEarned += outcome.fossils;
  }
  return outcome;
}
